import type { Role } from '@/lib/api/services/session';

/**
 * Governance artifacts (AI Constitution, AI Policy, Context Policies) may be
 * created, changed, published, or revoked only by an organization administrator.
 *
 * A null role means the session has not hydrated yet, and it is treated as NOT
 * an administrator. That is the opposite of the permissive `!r ||` convention
 * used elsewhere in settings: for governance, showing write controls to someone
 * who turns out not to be an admin produces a 403 on every click, so the safe
 * default is to withhold them until the role is known.
 *
 * This is a UI affordance only. The server independently re-checks the role and
 * the organization against the authoritative user record.
 */
export function isOrgAdminRole(role: Role | null | undefined): boolean {
  return role === 'org_admin' || role === 'super_admin';
}

/**
 * Three-state variant: `null` means the role is not known yet.
 *
 * `isOrgAdminRole` deliberately collapses "not hydrated" into "not an admin",
 * which is the right default for *withholding* controls but the wrong thing to
 * announce. Session bootstrap takes a few seconds, and during that window a real
 * administrator was being told, in as many words, that they are not allowed to
 * change anything. Callers that render an explanation should distinguish the two
 * and stay quiet until they actually know.
 */
export function orgAdminState(role: Role | null | undefined): boolean | null {
  return role == null ? null : isOrgAdminRole(role);
}
